import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'

interface ShortcutState {
  armedAt: number | null
  onShowHelp?: (() => void) | undefined
}

/** Time window for the second keystroke of a chord (e.g. `g` then `d`). */
const CHORD_WINDOW_MS = 1500

/**
 * Mapping of follow-up keys → destination routes after the user presses `g`.
 * Kept in sync with the sidebar nav order in AppShell.
 */
export const NAV_SHORTCUTS = {
  d: '/dashboard',
  p: '/projects',
  f: '/docfiller',
  r: '/prompts',
  w: '/workspaces',
  s: '/docfiller/chats',
} as const

export type NavShortcutKey = keyof typeof NAV_SHORTCUTS

/**
 * Human-readable labels for the shortcut help dialog and sidebar hints.
 */
export const NAV_SHORTCUT_LABELS: Record<NavShortcutKey, string> = {
  d: 'Dashboard',
  p: 'Projects',
  f: 'Doc Filler',
  r: 'Prompts',
  w: 'Workspaces',
  s: 'Search',
}

/** Targets where we never intercept keystrokes. */
function isInTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  // Radix dropdown/menu items live inside `[role=menu]` and consume their own
  // keys — don't fight them.
  if (target.closest('[role="menu"], [role="menuitem"]')) return true
  return false
}

interface UseNavShortcutsOptions {
  /** Called when the user presses `?` to open the help overlay. */
  onShowHelp?: () => void
  /** Toggle: pass false to disable the hook entirely. */
  enabled?: boolean
}

/**
 * Global keyboard navigation for the authenticated shell.
 *
 * - `g d` → dashboard, `g f` → docfiller, `g w` → workspaces,
 *   `g s` → document search. The first keystroke arms the chord; the second must land
 *   within 1.5s or the chord resets.
 * - `?` opens the keyboard-shortcuts help dialog (via `onShowHelp`).
 *
 * Modifier keys (Ctrl/Cmd/Alt/Meta) are intentionally ignored so we don't
 * fight browser shortcuts. Keystrokes inside inputs / textareas / menus are
 * also ignored.
 */
export function useNavShortcuts({
  onShowHelp,
  enabled = true,
}: UseNavShortcutsOptions = {}): void {
  const navigate = useNavigate()
  // Use a ref so the listener identity stays stable but we can still read
  // the freshest `onShowHelp` callback from inside it. The assignment lives
  // in an effect (not render) to satisfy `react-hooks/refs` — render-time
  // writes to refs trip the rule even though it would work in practice.
  const stateRef = useRef<ShortcutState>({
    armedAt: null,
    onShowHelp,
  })
  useEffect(() => {
    stateRef.current.onShowHelp = onShowHelp
  }, [onShowHelp])

  useEffect(() => {
    if (!enabled) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (isInTextInput(e.target)) return

      // `?` (Shift+/) — show help. Some keyboard layouts produce e.key === '?'
      // directly; others produce '/' with shiftKey. Accept both.
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault()
        stateRef.current.onShowHelp?.()
        stateRef.current.armedAt = null
        return
      }

      const key = e.key.toLowerCase()

      // First chord key
      if (key === 'g' && !e.shiftKey) {
        stateRef.current.armedAt = Date.now()
        return
      }

      // Second chord key
      const armedAt = stateRef.current.armedAt
      if (armedAt !== null && Date.now() - armedAt <= CHORD_WINDOW_MS) {
        stateRef.current.armedAt = null
        if (key in NAV_SHORTCUTS) {
          const to = NAV_SHORTCUTS[key as NavShortcutKey]
          e.preventDefault()
          void navigate({ to })
          return
        }
      }

      // Any other key cancels the chord.
      stateRef.current.armedAt = null
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [enabled, navigate])
}
